import React, { useCallback, useEffect, useState } from 'react';

import { Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import GoalProgressBar from 'components/GoalProgressBar';
import { openRealignModalAction } from 'components/RealignModal/redux/actions';
import { StyledAvatar } from 'components/avatars';
import TextParseComponent from 'components/feed/TextParseComponent';
import produce from 'immer';
import { isEmpty } from 'lodash';
import Task1 from 'models/task1';
import { useDispatch, useSelector } from 'react-redux';
import { StyledRealignIconComp } from 'screens/analytics/tableCellComponents';
import { StyledNodesView } from 'screens/common/sidepanel/sidepanelStyles';
import { selectGoalFromGlobalGoals } from 'selectors/globalGoalSelector';
import styled, { css } from 'styled-components';
import {
  StyledTypography,
  StyledGrid,
  PaddingCSS,
  FlexContainer,
  StyledIconButton,
  StyledTooltip,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { haveSamePropValues } from 'constants/helper';
import { GoalsIcon, KrSvgIcon, PrivateKRSvgIcon } from 'constants/icons';
import { GOAL_ANALYTICS_EVENTS, sendGoalAnalyticsEvents, sendOpenedKRPanelEvent } from 'constants/mixpanelHelper';
import { routesForTracking } from 'constants/routesForTracking';
import theme from 'constants/theme';

import { clearTeamsAndRemovedTeam } from 'services/helpers/task';
import { showErrorSnackbar } from 'services/snackbar';
import { createOrUpdateTask } from 'services/task';
import { showTaskDetails } from 'services/taskDetails';

import { Tline, EmptyBar } from '../../../commonComponents';

const ColoredIconButton = styled(StyledIconButton)`
  position: relative;
  background: ${theme.palette.icons.white};
  border: 1px solid ${theme.palette.icons.blue};
  width: 24px;
  height: 24px;
`;

function shouldRefetchGoalData(current, previous) {
  if (
    !haveSamePropValues(current, previous, [
      'detailedStatus',
      'goalProgressLevel',
      'goalProgressStatus',
      'startingProgressValue',
      'actual',
      'target',
    ])
  )
    return true;

  const currentToIds = current.toIds;
  const previousToIds = previous.toIds;

  if (currentToIds?.length !== previousToIds?.length) return true;

  currentToIds.forEach((cToId) => {
    if (!previousToIds.find((pToId) => pToId.id === cToId.id && pToId.ownerType === cToId.ownerType)) return true;
  });

  return false;
}

const fetchData = async ({ parentUuid, indexParam, fetchObjectiveData, fetchGoalsData, rows }) => {
  const indexValue = indexParam;
  const rowObject = rows[indexValue];

  sendGoalAnalyticsEvents({
    eventName: GOAL_ANALYTICS_EVENTS.expandedObjective,
    page: {
      pageName: routesForTracking.analyticsGoals.pageName,
      flow: 'Activity',
    },
    groupBy: 'Objective',
    objectiveId: parentUuid,
  });

  try {
    const res = await Promise.all([
      fetchObjectiveData(parentUuid)
        .then((res) => res)
        .catch((err) => {
          console.error('Error fetching objective data : ', err);
        }),
      fetchGoalsData(parentUuid).then((res) => res),
    ]);
    const updatedRowObject = {
      ...rowObject,
      children: res[0]
        ? res[0].values.map((rObj) => ({
            dataObj: rObj,
            level: rowObject.level + 1,
            parentUuid,
            children: null,
          }))
        : null,
      goals: res[1].length ? res[1] : null,
    };

    return { index: indexValue, newObj: updatedRowObject };
  } catch (err) {
    console.error('Error in fetching objective data : ', err);
  }
};

const recursiveFetch = async (arr = [], fetchObjectiveData, fetchGoalsData, rows, prev = {}) => {
  // arrStructure = [{index, uuid, children: {}}]

  try {
    const res = await Promise.all(
      arr.map(async ({ uuid, index, children }) => {
        const res = await fetchData({
          parentUuid: uuid,
          indexParam: index,
          fetchObjectiveData,
          fetchGoalsData,
          rows,
        });
        if (Object.keys(children).length) {
          const response = await recursiveFetch(
            Object.values(children),
            fetchObjectiveData,
            fetchGoalsData,
            rows[index].children,
            res
          );
          return response;
        }
        if (isEmpty(prev)) {
          return res;
        } else {
          const children = [...prev.newObj.children];
          children[res.index] = res.newObj;
          const response = { index: prev.index, newObj: { ...prev.newObj, children } };
          return response;
        }
      })
    );
    return res;
  } catch (err) {
    console.error('Error in recursive fetch : ', err);
  }
};

const makeRowObj = (rowObject, index, arr = []) => {
  const modifiedChildren = [...rowObject.children];
  if (modifiedChildren?.length > 0) {
    arr.forEach((obj) => {
      if (Array.isArray(obj)) {
        obj.forEach(({ index, newObj }) => {
          modifiedChildren[index] = newObj;
        });
      } else {
        modifiedChildren[obj?.index] = obj?.newObj;
      }
    });
  }
  return { index, newObj: { ...rowObject, children: modifiedChildren } };
};

export const onObjectiveModificationSuccess = async ({
  expandedRowIds,
  fetchObjectiveData,
  fetchGoalsData,
  rows,
  updateRowDataModifiedForArray,
}) => {
  const res = await Promise.all(
    Object.keys(expandedRowIds).map(async (k) => {
      if (!Object.keys(expandedRowIds[k].children).length) {
        const value = await fetchData({
          parentUuid: k,
          indexParam: expandedRowIds[k].index,
          fetchObjectiveData,
          fetchGoalsData,
          rows,
        });
        return value;
      }
      const parentNewValue = await fetchData({
        parentUuid: k,
        indexParam: expandedRowIds[k].index,
        fetchObjectiveData,
        fetchGoalsData,
        rows,
      });
      const res = await recursiveFetch(
        Object.values(expandedRowIds[k].children),
        fetchObjectiveData,
        fetchGoalsData,
        rows[expandedRowIds[k].index].children
      );
      const value = makeRowObj(parentNewValue.newObj, expandedRowIds[k].index, res);
      return value;
    })
  );
  if (updateRowDataModifiedForArray && typeof updateRowDataModifiedForArray === 'function') {
    updateRowDataModifiedForArray(res);
  }
};

const RealignHoverIconForGoals = ({ onRealignClick = () => {} }) => {
  // realign hover state
  // keeping them here cause if hovered on the hover button, the parent's hover state turns false

  const [pointerLocation, setPointerLocation] = useState({
    elem: false,
    hover: false,
  });

  const handleMouseEnter = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, elem: true }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, elem: false }));
  }, []);

  const handleHoverMouseEnter = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, hover: true }));
  }, []);

  const handleHoverMouseLeave = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, hover: false }));
  }, []);

  return <StyledRealignIconComp onClick={onRealignClick} />;
};

export const columnList = [
  {
    id: 'name',
    component: (
      goal,
      level,
      subObjectivesLength,
      isLastElement,
      isLastObjectivesGoal,
      levelsForWhichLinesToBeDrawn,
      onRealignClick = () => {}
    ) => {
      const isStrikedThroughStatus =
        (goal?.goalClosureStatus === 'DROPPED' ||
          goal?.goalClosureStatus === 'ACHIEVED' ||
          goal?.goalClosureStatus === 'NOT_ACHIEVED') &&
        goal?.status === 'COMPLETED';
      return (
        <StyledTableCell width="auto" style={{ position: 'relative' }}>
          {
            // to draw vertical lines only for required levels
          }
          {levelsForWhichLinesToBeDrawn.map((_level, i) => (
            <Tline
              key={_level}
              half={_level === level && subObjectivesLength === 0 && isLastElement}
              noHorizontal={_level < level}
              level={_level}
            />
          ))}
          {
            // the following line is for current level vertical line and horizontal line
          }
          <Tline half={subObjectivesLength === 0 && isLastElement} level={level} forceOldValue />
          <FlexContainer style={{ 'max-width': '45vw' }}>
            <StyledTypography
              wordBreak
              variant="body2"
              textDecorationColor="textPrimary"
              color={theme.palette.text.heading}
              display="flex"
              truncate
              style={{
                marginLeft: `calc(${29 * level}px + ${2 * level * 3}rem)`,
                display: 'flex',
              }}
            >
              <StyledTooltip title={goal.text.length > 240 ? goal.text : ''}>
                <VerticallyAlignedFlexContainer>
                  <RealignHoverIconForGoals onRealignClick={onRealignClick} />
                  {goal?.visibilityType === 'PRIVATE' ? <PrivateKRIcon /> : <StyledKRIcon />}
                  <div
                    style={{
                      overflow: 'hidden',
                      display: '-webkit-box',
                      webkitLineClamp: '2',
                      webkitBoxOrient: 'vertical',
                    }}
                  >
                    <TextParseComponent
                      isMultiLineTruncate
                      text={goal.text}
                      res={goal.macros}
                      isStrikedThrough={isStrikedThroughStatus}
                      isGreyedOut={goal?.goalClosureStatus === 'DROPPED'}
                    />
                  </div>
                </VerticallyAlignedFlexContainer>
              </StyledTooltip>
            </StyledTypography>
          </FlexContainer>
        </StyledTableCell>
      );
    },
  },
  // {
  //   id: 'subObjective',
  //   component: goal => {
  //     return (
  //       <StyledTableCell center width="15%">
  //         <StyledGrid container justify="center">
  //           {goal.subObjective ? (
  //             <StyledTypography variant="body1" color="textSecondary">
  //               {goal.subObjective.name}
  //             </StyledTypography>
  //           ) : (
  //             <EmptyBar />
  //           )}
  //         </StyledGrid>
  //       </StyledTableCell>
  //     );
  //   }
  // },
  {
    id: 'reportees',
    component: (goal) => (
      <StyledTableCell center style={{ width: '20rem' }} paddingTop="18px" paddingBottom="13px">
        {goal.toIds && goal.toIds.length > 0 ? (
          <StyledNodesView
            employees={goal.toIds}
            maxNodes={4}
            useEmployeeClick={false}
            renderAvatar={(props) => (
              <StyledAvatar
                {...props}
                size="smallest"
                leftGutter={-1}
                border={
                  props?.ownerType === Task1.EMPLOYEE_TYPES.owner
                    ? `2px solid ${theme.palette.green.main}`
                    : '2px solid #EAEAEA'
                }
              />
            )}
            onClick={(e) => {
              e.preventDefault();
            }}
          />
        ) : (
          <FlexContainer style={{ justifyContent: 'center' }}>
            <EmptyBar />
          </FlexContainer>
        )}
      </StyledTableCell>
    ),
  },
  {
    id: 'progress',
    component: (goal) => (
      <StyledTableCell
        center
        style={{
          width: '20rem',
          padding: '1rem 1.5rem 1rem 2rem',
        }}
      >
        <FlexContainer style={{ alignItems: 'center' }}>
          <GoalProgressBar width="82%" goal={goal} />
          <StyledTypography variant="body2" display="inline" style={{ marginLeft: 'auto' }}>
            {`${Number(goal.getGoalProgress(0) || 0)}%`}
          </StyledTypography>
        </FlexContainer>
      </StyledTableCell>
    ),
  },
];

const ObjectiveAndGoalRow = ({
  goal: localGoal,
  index,
  showDetails = () => {},
  level,
  subObjectivesLength,
  data = [],
  setData,
  isLastObjectivesGoal,
  levelsForWhichLinesToBeDrawn,
  dispatch,
  refetchData,
  expandedRowIds,
  fetchObjectiveData,
  fetchGoalsData,
  rows,
  updateRowDataModifiedForArray,
  currentPageNumber,
  isTeamHubView = false,
}) => {
  const globalGoal = useSelector(selectGoalFromGlobalGoals(localGoal?.id)) ?? localGoal;
  const { subObjective: selectedObjective } = globalGoal ?? {};

  const updateGoal = async (task, reloadData = true) => {
    try {
      task = clearTeamsAndRemovedTeam(task);
      const updatedTask = await createOrUpdateTask({ ...task, overrideToIds: false });

      updatedTask.createdDate = task.createdDate;
      return updatedTask;
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || 'There is some problem. Please try again!'));
    }
  };

  const onSelectHandler = async (tag) => {
    try {
      const res = await updateGoal({ ...globalGoal, subObjective: tag });
      // hacky way to unblock QA team for now
      // timeout shouldn't be needed when API calls go one after another
      setTimeout(async () => {
        await refetchData({
          pageNumber: currentPageNumber,
        });
      }, 500);
      return res;
    } catch (err) {
      console.error('Error in modifying objective : ', err);
    }
  };

  const onRealignClick = (e) => {
    e.stopPropagation();
    dispatch(
      openRealignModalAction({
        objectiveId: selectedObjective?.objectiveId,
        realignId: selectedObjective?.objectiveTimeBoundId,
        // objectiveName: selectedObjective?.displayName,
        selectedTimeBoundPeriod: selectedObjective?.timeBoundDetails,
        selectedObjective: {
          timeBoundDetails: selectedObjective?.timeBoundDetails,
          objectiveId: selectedObjective?.objectiveId,
        },
        onSelect: onSelectHandler,
        source: isTeamHubView ? 'Team Page' : 'Insights Page Overall',
        krName: globalGoal?.text,
      })
    );
  };

  useEffect(() => {
    if (globalGoal) {
      setData((prevData) => {
        const updatedDataArray = produce(prevData, (draft) => {
          const firstHalf = draft.slice(0, index);
          const secondHalf = draft.slice(index + 1);
          return [...firstHalf, globalGoal, ...secondHalf];
        });
        return updatedDataArray;
      });

      if (shouldRefetchGoalData(globalGoal, localGoal)) {
        refetchData({
          pageNumber: currentPageNumber,
          onSuccess: async () => {
            onObjectiveModificationSuccess({
              expandedRowIds,
              fetchObjectiveData,
              fetchGoalsData,
              rows,
            });
          },
        });
      }
    }
    if (globalGoal?.subObjective?.id !== localGoal?.subObjective?.id) {
      refetchData({
        pageNumber: currentPageNumber,
        onSuccess: async () => {
          onObjectiveModificationSuccess({
            expandedRowIds,
            fetchObjectiveData,
            fetchGoalsData,
            rows,
            updateRowDataModifiedForArray,
          });
        },
      });
    }
  }, [globalGoal]);

  return (
    <StyledTableRow
      hoverable
      key={`objective-and-goal-list-${globalGoal.id}`}
      onClick={() => showDetails(index, globalGoal.id, globalGoal.type, globalGoal)}
    >
      {columnList.map((col) =>
        col.component(
          globalGoal,
          level,
          subObjectivesLength,
          index === data.length - 1,
          isLastObjectivesGoal,
          levelsForWhichLinesToBeDrawn,
          onRealignClick
        )
      )}
    </StyledTableRow>
  );
};

const ObjectiveAndGoalsList = ({
  loading,
  goals = [],
  level,
  subObjectivesLength,
  isLastObjectivesGoal,
  levelsForWhichLinesToBeDrawn,
  refetchData,
  currentPageNumber,
  expandRow,
  parentObjective,
  expandedRowIds = [],
  fetchObjectiveData,
  fetchGoalsData,
  rows,
  updateRowDataModifiedForArray,
  $isExpanded,
  isTeamHubView = false,
}) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(goals);

  useEffect(() => {
    setData(goals);
  }, [goals]);

  const showDetails = (index, taskId, type, originalGoal) => {
    sendOpenedKRPanelEvent({
      eventProperties: {
        source: isTeamHubView ? 'Team Page' : 'Insights Page Overall',
      },
    });
    dispatch(
      showTaskDetails({
        taskIds: [taskId],
        onDelete: null,
        employee: {},
        type,
      })
    );
  };

  return (
    <>
      {loading ? (
        <LoadingGoals />
      ) : (
        <StyledTable aria-label="custom pagination table">
          <TableBody>
            {data.map((goal, index) => (
              <ObjectiveAndGoalRow
                goal={goal}
                index={index}
                key={`objective-and-goal-list-${index}`}
                showDetails={showDetails}
                level={level}
                subObjectivesLength={subObjectivesLength}
                data={data}
                setData={setData}
                isLastObjectivesGoal={isLastObjectivesGoal}
                levelsForWhichLinesToBeDrawn={levelsForWhichLinesToBeDrawn}
                dispatch={dispatch}
                refetchData={refetchData}
                expandedRowIds={expandedRowIds}
                fetchObjectiveData={fetchObjectiveData}
                fetchGoalsData={fetchGoalsData}
                rows={rows}
                updateRowDataModifiedForArray={updateRowDataModifiedForArray}
                currentPageNumber={currentPageNumber}
                isTeamHubView={isTeamHubView}
              />
            ))}
          </TableBody>
        </StyledTable>
      )}
    </>
  );
};

const LoadingGoals = () => (
  <StyledTableRow>
    {columnList.map((col) => (
      <StyledTableCell align="center">
        <StyledGrid container justify="center" alignItems="center">
          <Skeleton width="80%" />
        </StyledGrid>
      </StyledTableCell>
    ))}
  </StyledTableRow>
);

const StyledTable = styled(Table)`
  margin: 0;
`;

const StyledTableCell = styled(TableCell)`
  border-bottom: none;
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'inherit')};
  ${({ center }) =>
    center &&
    `
    text-align: center;
  `};
  ${PaddingCSS};
`;

const StyledTableRow = styled(TableRow)`
  & * {
    cursor: pointer;
  }
  &:last-child {
    td {
      border-bottom: none;
    }
  }

  ${(props) =>
    props.hoverable &&
    css`
      &:hover {
        background-color: ${(props) => props.theme.palette.background.lightBlue};
        ${StyledRealignIconComp} {
          visibility: visible;
        }
      }
    `}
`;

const StyledGoalsIcon = styled(GoalsIcon)`
  margin-right: 1rem;
  vertical-align: sub;

  svg {
    fill: ${(props) => props.theme.palette.grey.main};
    width: 18px;
    height: 18px;
  }
`;

const PrivateKRIcon = styled(PrivateKRSvgIcon)`
  margin-right: 1rem;
  vertical-align: sub;
  svg {
    fill: ${(props) => props.theme.palette.icons.krBlue};
    width: 18px;
    height: 18px;
  }
`;

const StyledKRIcon = styled(KrSvgIcon)`
  margin-right: 1rem;
  vertical-align: sub;
  svg {
    fill: ${(props) => props.theme.palette.icons.krBlue};
    width: 18px;
    height: 18px;
  }
`;

export default ObjectiveAndGoalsList;
